import React, { useCallback } from "react";
import { Form } from "antd";
import { useSelector } from "react-redux";

import { RadioItem as BaseRadioItem } from "./RadioItem";
import useConditionalValidation from "./utils/useConditionalValidation";
import { selectPatientDetails } from "../../../../../store/selector";
import { getNamesFromFullName } from "../../utils";
import { buildFieldValueMap } from "../../../../../utils/form";

const CAREGIVER_SELECTION = {
  YES: true,
  NO: false
};

const extractCaregiverDataFromPatient = (patientDetails) => {
  const fullNameCaregiver = patientDetails?.fullNameCaregiver;
  const { firstName, lastName } = fullNameCaregiver
    ? getNamesFromFullName(fullNameCaregiver)
    : { firstName: undefined, lastName: undefined };

  return {
    phone: patientDetails?.phoneCaregiver || undefined,
    relationship: patientDetails?.relationToPatientCaregiver ? "other" : undefined,
    email: patientDetails?.emailCaregiver || undefined,
    firstName: firstName || undefined,
    lastName: lastName || undefined,
    relationshipOther: patientDetails?.relationToPatientCaregiver || undefined
  };
};

const createFieldMappings = (formToFieldMap, caregiverData) => {
  return formToFieldMap
    .map(([fieldPath, caregiverFieldKey]) => {
      const value = caregiverData[caregiverFieldKey];
      const hasValidValue = value !== undefined && value !== null && value !== "";
      return hasValidValue ? [fieldPath, value] : null;
    })
    .filter(Boolean); // Remove null entries
};

export const CaregiverRadioItem = ({
  name,
  label,
  required = false,
  rules = [],
  optionItems,
  fieldProps = {},
  formItemProps = {},
  groupStyle = {},
  direction = "horizontal",
  shouldValidateFields = false,
  formToFieldMap = [],
  ...props
}) => {
  const formInstance = Form.useFormInstance();
  const patientDetails = useSelector(selectPatientDetails);

  const caregiverData = extractCaregiverDataFromPatient(patientDetails);

  useConditionalValidation(name, shouldValidateFields);

  const fillCaregiverFields = useCallback(() => {
    if (!formToFieldMap.length) return;

    const fieldMappings = createFieldMappings(formToFieldMap, caregiverData);

    if (fieldMappings.length > 0) {
      const fieldsToUpdate = buildFieldValueMap(fieldMappings);
      formInstance.setFieldsValue(fieldsToUpdate);
    }
  }, [formToFieldMap, caregiverData, formInstance]);

  const handleRadioChange = useCallback(
    (value) => {
      if (value === CAREGIVER_SELECTION.YES) {
        fillCaregiverFields();
      }
    },
    [fillCaregiverFields, formInstance, formToFieldMap]
  );

  return (
    <BaseRadioItem
      name={name}
      label={label}
      required={required}
      rules={rules}
      optionItems={optionItems}
      fieldProps={{
        ...fieldProps,
        onChange: (e) => {
          handleRadioChange(e.target.value);
          if (fieldProps.onChange) {
            fieldProps.onChange(e);
          }
        }
      }}
      formItemProps={formItemProps}
      groupStyle={groupStyle}
      direction={direction}
      shouldValidateFields={shouldValidateFields}
      {...props}
    />
  );
};
